import { z } from 'zod';

export const WORD_CHANT_VISUAL_PRESET_IDS = [
  'word-chant-classroom-v1',
  'word-chant-color-pop-v1',
  'word-chant-paper-play-v1',
] as const;

export const WORD_CHANT_FONT_IDS = [
  'fredoka',
  'suit',
  'freesentation',
] as const;

// 녹음형 Word Chant의 화면 공통 문구는 세 시각 템플릿 모두 Fredoka를 사용한다.
// LINE Seed는 Remotion의 spoken chant word 전용 분기에서만 적용한다.
export const WORD_CHANT_DISPLAY_FONT_ID = 'fredoka' as const;

export const WORD_CHANT_TEXT_SCALES = ['small', 'medium', 'large'] as const;

export const WORD_CHANT_PALETTE_IDS = [
  'classroom-blue',
  'classroom-mint',
  'color-pop-primary',
  'color-pop-candy',
  'paper-play-warm',
  'paper-play-cool',
] as const;

export const WORD_CHANT_MICROPHONE_STYLE_IDS = [
  'classic-gold',
  'orange-pop',
  'pink-pop',
] as const;

export type WordChantVisualPresetId =
  (typeof WORD_CHANT_VISUAL_PRESET_IDS)[number];
export type WordChantFontId = (typeof WORD_CHANT_FONT_IDS)[number];
export type WordChantTextScale = (typeof WORD_CHANT_TEXT_SCALES)[number];
export type WordChantPaletteId = (typeof WORD_CHANT_PALETTE_IDS)[number];
export type WordChantMicrophoneStyleId =
  (typeof WORD_CHANT_MICROPHONE_STYLE_IDS)[number];

interface WordChantVisualPresetDefinition {
  presetVersion: 1;
  defaultFontId: WordChantFontId;
  allowedFontIds: readonly WordChantFontId[];
  defaultPaletteId: WordChantPaletteId;
  allowedPaletteIds: readonly WordChantPaletteId[];
}

export const WORD_CHANT_VISUAL_PRESETS = {
  'word-chant-classroom-v1': {
    presetVersion: 1,
    defaultFontId: WORD_CHANT_DISPLAY_FONT_ID,
    allowedFontIds: ['fredoka', 'suit', 'freesentation'],
    defaultPaletteId: 'classroom-blue',
    allowedPaletteIds: ['classroom-blue', 'classroom-mint'],
  },
  'word-chant-color-pop-v1': {
    presetVersion: 1,
    defaultFontId: WORD_CHANT_DISPLAY_FONT_ID,
    allowedFontIds: ['fredoka', 'freesentation'],
    defaultPaletteId: 'color-pop-primary',
    allowedPaletteIds: ['color-pop-primary', 'color-pop-candy'],
  },
  'word-chant-paper-play-v1': {
    presetVersion: 1,
    defaultFontId: WORD_CHANT_DISPLAY_FONT_ID,
    // 기존 paper-play 저장값은 파싱하되 신규 선택/재생성 시 공통 Fredoka로 canonicalize한다.
    allowedFontIds: ['fredoka', 'suit', 'freesentation'],
    defaultPaletteId: 'paper-play-warm',
    allowedPaletteIds: ['paper-play-warm', 'paper-play-cool'],
  },
} as const satisfies Record<
  WordChantVisualPresetId,
  WordChantVisualPresetDefinition
>;

export const wordChantStyleSchema = z
  .object({
    version: z.literal('word_chant.style.v1'),
    presetId: z.enum(WORD_CHANT_VISUAL_PRESET_IDS),
    presetVersion: z.literal(1),
    displayTitle: z.string().trim().min(1).max(60),
    fontId: z.enum(WORD_CHANT_FONT_IDS),
    textScale: z.enum(WORD_CHANT_TEXT_SCALES),
    paletteId: z.enum(WORD_CHANT_PALETTE_IDS),
    microphoneStyleId: z
      .enum(WORD_CHANT_MICROPHONE_STYLE_IDS)
      .default('orange-pop'),
  })
  .strict()
  .superRefine((style, context) => {
    const preset = WORD_CHANT_VISUAL_PRESETS[style.presetId];
    if (!(preset.allowedFontIds as readonly string[]).includes(style.fontId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fontId'],
        message: 'fontId is not allowed for presetId',
      });
    }
    if (
      !(preset.allowedPaletteIds as readonly string[]).includes(style.paletteId)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paletteId'],
        message: 'paletteId is not allowed for presetId',
      });
    }
  });

export type WordChantStyle = z.infer<typeof wordChantStyleSchema>;

export function createWordChantStyleForPreset(
  presetId: WordChantVisualPresetId,
): WordChantStyle {
  const preset = WORD_CHANT_VISUAL_PRESETS[presetId];
  return {
    version: 'word_chant.style.v1',
    presetId,
    presetVersion: preset.presetVersion,
    displayTitle: 'My Word Chant',
    fontId: preset.defaultFontId,
    textScale: 'medium',
    paletteId: preset.defaultPaletteId,
    microphoneStyleId: 'orange-pop',
  };
}

export const DEFAULT_WORD_CHANT_STYLE = createWordChantStyleForPreset(
  'word-chant-classroom-v1',
);
